import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function initials(name) {
  if (!name) return 'SD';
  return name.split(' ').filter(Boolean).slice(0, 2).map(s => s[0]?.toUpperCase()).join('') || 'SD';
}

export function pct(n) {
  if (n == null) return '0%';
  return `${Math.round(Number(n))}%`;
}

export function colorForScore(score) {
  if (score >= 7) return '#4CD964';
  if (score >= 5) return '#D4943A';
  return '#C94040';
}

export function labelForScore(score) {
  if (score >= 8.5) return 'Exceptional';
  if (score >= 7) return 'Strong';
  if (score >= 5) return 'Competitive';
  if (score >= 3) return 'Developing';
  return 'Gap';
}

/**
 * Extract a clean search-friendly role title by stripping everything after an
 * em-dash / en-dash / " — " / " – " / " - " separator. LinkedIn and Google job
 * searches work best on the pure role title (e.g. "Senior Climate Risk Analyst")
 * without the industry / company-size suffix Claude often adds (e.g.
 * "Senior Climate Risk Analyst — Financial Services" or
 * "VP Sustainable Finance & ESG Credit, Asset Management").
 *
 * Also strips trailing ", <sector>" and " · <sector>" suffixes when the sector
 * is 3+ words long (a "sub-title" style suffix).
 *
 * Never returns empty — falls back to the original title if all strip attempts
 * would leave nothing.
 */
export function cleanSearchTitle(title) {
  if (!title) return '';
  let t = String(title).trim();

  // Strip suffix after em-dash / en-dash / hyphen (with surrounding whitespace)
  // Handles: "Senior Analyst — Climate Tech" | "Analyst – Renewables" | "Analyst - Fintech"
  const dashMatch = t.match(/^([^—–]+?)(?:\s+[—–]\s+.+)$/);
  if (dashMatch) t = dashMatch[1].trim();
  // Also handle ASCII " - " suffix but ONLY when the prefix has 3+ words (avoids
  // eating hyphenated titles like "Vice-President" that don't have spaces).
  const asciiDash = t.match(/^(.+?)\s+-\s+(.+)$/);
  if (asciiDash && asciiDash[1].split(/\s+/).length >= 2) t = asciiDash[1].trim();

  // Strip trailing ", <suffix>" ONLY when both the prefix looks like a complete
  // role title (3+ words) AND the suffix reads like a sector clause (3+ words).
  // This preserves short titles like "VP, Sustainable Finance" and
  // "Director, Latin America Climate Policy" where the comma is part of the title.
  const commaMatch = t.match(/^(.+?),\s+([^,]+)$/);
  if (
    commaMatch
    && commaMatch[1].split(/\s+/).length >= 3
    && commaMatch[2].split(/\s+/).length >= 3
  ) t = commaMatch[1].trim();

  return t || String(title).trim();
}

/* ---------------------------------------------------------------------
   Country → language mapping for localized job-title search.
   Non-English speakers hit the LinkedIn/Google jobs boards in their own
   language. Searching for "Director of Sustainability" in Mexico returns
   almost no hits; searching for "Directora de Sustentabilidad" returns
   the actual market. All UI stays in English — we only translate the
   `keywords=` parameter on outbound search links.
   --------------------------------------------------------------------- */
const COUNTRY_TO_LANG = {
  // Spanish-speaking
  mexico: 'es', mx: 'es',
  spain: 'es', es: 'es',
  argentina: 'es', ar: 'es',
  chile: 'es', cl: 'es',
  colombia: 'es', co: 'es',
  peru: 'es', pe: 'es',
  uruguay: 'es', uy: 'es',
  ecuador: 'es', ec: 'es',
  costa_rica: 'es', 'costa rica': 'es',
  dominican_republic: 'es', 'dominican republic': 'es',
  panama: 'es', pa: 'es',
  bolivia: 'es', bo: 'es',
  guatemala: 'es', gt: 'es',
  paraguay: 'es', py: 'es',
  venezuela: 'es', ve: 'es',
  // Portuguese
  brazil: 'pt', br: 'pt', brasil: 'pt',
  portugal: 'pt', pt: 'pt',
  // French
  france: 'fr', fr: 'fr',
  belgium: 'fr', be: 'fr',
  luxembourg: 'fr', lu: 'fr',
  // German
  germany: 'de', de: 'de', deutschland: 'de',
  austria: 'de', at: 'de',
  switzerland: 'de', ch: 'de',
  // Italian
  italy: 'it', it: 'it', italia: 'it',
  // Dutch
  netherlands: 'nl', nl: 'nl',
};

/* Common sustainability / cross-domain role-title translations. Keyed by
   language code, value maps lowercase English title fragments → localized
   equivalents. Order-preserving longest-first ensures "director of
   sustainability" wins over "director" alone. */
const TITLE_TRANSLATIONS = {
  es: [
    ['head of sustainability', 'director de sustentabilidad'],
    ['director of sustainability', 'director de sustentabilidad'],
    ['director of marketing', 'director de marketing'],
    ['chief sustainability officer', 'director general de sustentabilidad'],
    ['chief marketing officer', 'director general de marketing'],
    ['sustainability manager', 'gerente de sustentabilidad'],
    ['senior sustainability manager', 'gerente senior de sustentabilidad'],
    ['sustainability analyst', 'analista de sustentabilidad'],
    ['esg analyst', 'analista de esg'],
    ['esg manager', 'gerente de esg'],
    ['climate risk analyst', 'analista de riesgo climático'],
    ['climate risk manager', 'gerente de riesgo climático'],
    ['head of esg', 'director de esg'],
    ['circular economy manager', 'gerente de economía circular'],
    ['sustainable finance', 'finanzas sostenibles'],
    ['sustainability', 'sustentabilidad'],
    ['climate', 'clima'],
    ['director', 'director'],
    ['manager', 'gerente'],
    ['analyst', 'analista'],
    ['head', 'director'],
    ['vice president', 'vicepresidente'],
    ['vp', 'vicepresidente'],
  ],
  pt: [
    ['head of sustainability', 'diretor de sustentabilidade'],
    ['director of sustainability', 'diretor de sustentabilidade'],
    ['director of marketing', 'diretor de marketing'],
    ['chief sustainability officer', 'diretor geral de sustentabilidade'],
    ['chief marketing officer', 'diretor geral de marketing'],
    ['sustainability manager', 'gerente de sustentabilidade'],
    ['senior sustainability manager', 'gerente sênior de sustentabilidade'],
    ['sustainability analyst', 'analista de sustentabilidade'],
    ['esg analyst', 'analista de esg'],
    ['esg manager', 'gerente de esg'],
    ['climate risk analyst', 'analista de risco climático'],
    ['head of esg', 'diretor de esg'],
    ['circular economy manager', 'gerente de economia circular'],
    ['sustainable finance', 'finanças sustentáveis'],
    ['sustainability', 'sustentabilidade'],
    ['climate', 'clima'],
    ['director', 'diretor'],
    ['manager', 'gerente'],
    ['analyst', 'analista'],
    ['head', 'diretor'],
    ['vice president', 'vice-presidente'],
    ['vp', 'vice-presidente'],
  ],
  fr: [
    ['head of sustainability', 'directeur du développement durable'],
    ['director of sustainability', 'directeur du développement durable'],
    ['director of marketing', 'directeur marketing'],
    ['chief sustainability officer', 'directeur général du développement durable'],
    ['chief marketing officer', 'directeur général marketing'],
    ['sustainability manager', 'responsable développement durable'],
    ['sustainability analyst', 'analyste développement durable'],
    ['esg analyst', 'analyste esg'],
    ['esg manager', 'responsable esg'],
    ['climate risk analyst', 'analyste risque climatique'],
    ['head of esg', 'directeur esg'],
    ['sustainable finance', 'finance durable'],
    ['sustainability', 'développement durable'],
    ['climate', 'climat'],
    ['director', 'directeur'],
    ['manager', 'responsable'],
    ['analyst', 'analyste'],
    ['head', 'directeur'],
    ['vice president', 'vice-président'],
    ['vp', 'vice-président'],
  ],
  de: [
    ['head of sustainability', 'leiter nachhaltigkeit'],
    ['director of sustainability', 'director nachhaltigkeit'],
    ['director of marketing', 'marketingdirektor'],
    ['chief sustainability officer', 'chief sustainability officer'],  // widely used in DE
    ['chief marketing officer', 'chief marketing officer'],
    ['sustainability manager', 'nachhaltigkeitsmanager'],
    ['sustainability analyst', 'nachhaltigkeitsanalyst'],
    ['esg analyst', 'esg-analyst'],
    ['esg manager', 'esg-manager'],
    ['climate risk analyst', 'klimarisikoanalyst'],
    ['head of esg', 'leiter esg'],
    ['sustainable finance', 'nachhaltige finanzen'],
    ['sustainability', 'nachhaltigkeit'],
    ['climate', 'klima'],
    ['director', 'director'],
    ['manager', 'manager'],
    ['analyst', 'analyst'],
    ['head', 'leiter'],
    ['vice president', 'vice president'],
    ['vp', 'vice president'],
  ],
  it: [
    ['head of sustainability', 'direttore sostenibilità'],
    ['director of sustainability', 'direttore sostenibilità'],
    ['director of marketing', 'direttore marketing'],
    ['sustainability manager', 'sustainability manager'],  // widely borrowed in IT
    ['sustainability analyst', 'analista sostenibilità'],
    ['esg analyst', 'analista esg'],
    ['esg manager', 'esg manager'],
    ['sustainable finance', 'finanza sostenibile'],
    ['sustainability', 'sostenibilità'],
    ['climate', 'clima'],
    ['director', 'direttore'],
    ['manager', 'manager'],
    ['analyst', 'analista'],
    ['head', 'direttore'],
  ],
  nl: [
    ['head of sustainability', 'director duurzaamheid'],
    ['sustainability manager', 'duurzaamheidsmanager'],
    ['sustainability analyst', 'duurzaamheidsanalist'],
    ['sustainability', 'duurzaamheid'],
    ['climate', 'klimaat'],
    ['director', 'director'],
    ['manager', 'manager'],
    ['analyst', 'analist'],
    ['head', 'hoofd'],
  ],
};

function _detectLanguage(locationOrCountry) {
  if (!locationOrCountry) return null;
  const norm = String(locationOrCountry).toLowerCase().trim();
  // Direct match
  if (COUNTRY_TO_LANG[norm]) return COUNTRY_TO_LANG[norm];
  // Search for any known country name as a substring of the location string
  // (e.g. "Mexico City, Mexico" → "mexico" match).
  for (const [country, lang] of Object.entries(COUNTRY_TO_LANG)) {
    if (country.length > 2 && norm.includes(country)) return lang;
  }
  return null;
}

/* Translate an English job title into the local language of the target
   search country. Falls back to `cleanSearchTitle(title)` (English) when
   no translation is available. Case-insensitive matching.
*/
export function localizedSearchTitle(title, locationOrCountry) {
  const cleaned = cleanSearchTitle(title);
  if (!cleaned) return '';
  const lang = _detectLanguage(locationOrCountry);
  if (!lang) return cleaned;
  const dict = TITLE_TRANSLATIONS[lang];
  if (!dict) return cleaned;
  const lower = cleaned.toLowerCase();
  // Try LONGEST match first — so "director of sustainability" beats "director".
  const sorted = [...dict].sort((a, b) => b[0].length - a[0].length);
  for (const [en, local] of sorted) {
    if (lower.includes(en)) {
      // Preserve the surrounding context (e.g. "senior director of
      // sustainability" → "senior director de sustentabilidad") — replace
      // in-place, case-insensitive.
      const idx = lower.indexOf(en);
      return (
        cleaned.slice(0, idx) + local + cleaned.slice(idx + en.length)
      );
    }
  }
  return cleaned;
}
